import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6"

import Button from "@/components/Button/Button"
import DirectionText from "@/components/DirectionText/DirectionText"
import MyModal from "@/components/MyModal/MyModal"
import { MODALS } from "@/constants/modals"
import {
  useTrackClickAdMutation,
  useTrackCloseAdMutation,
} from "@/queries/mutations"
import { useRelevantAdvertisement } from "@/queries/queries"
import { useStore } from "@/zustand/store"

import styles from "./AdvertisementModal.module.css"

export default function AdvertisementModal() {
  const { t, i18n } = useTranslation()
  const adModalShown = useStore((state) => state.adModalShown)
  const setAdModalShown = useStore((state) => state.setAdModalShown)
  const authData = useStore((state) => state.authData)

  const { data: advertisement } = useRelevantAdvertisement({
    enabled: Boolean(authData),
  })

  const trackClick = useTrackClickAdMutation()
  const trackClose = useTrackCloseAdMutation()

  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    // Show modal only if:
    // 1. User is authenticated
    // 2. There's an advertisement
    // 3. Modal hasn't been shown yet in this session

    if (
      authData &&
      advertisement &&
      !advertisement?.user_seen_ad &&
      !adModalShown
    ) {
      setIsOpen(true)
      setAdModalShown(true)
    }
  }, [authData, advertisement, adModalShown, setAdModalShown])

  const handleCTAClick = () => {
    if (advertisement?.cta_link) {
      trackClick.mutate(advertisement)
      window.open(advertisement.cta_link, "_blank", "noopener,noreferrer")
    }
  }

  if (!advertisement) {
    return null
  }

  return (
    <MyModal
      modalName={MODALS.advertisements.index}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      size="xl"
      onClose={() => {
        trackClose.mutate(advertisement)
        setIsOpen(false)
      }}
      title={
        <DirectionText>
          {advertisement.title || t("advertisements.title")}
        </DirectionText>
      }
    >
      <div className={styles.modalContent}>
        {advertisement.image && (
          <div className={styles.imageContainer}>
            <img
              src={advertisement.image}
              alt={advertisement.title}
              className={styles.image}
            />
          </div>
        )}

        <div className={styles.textContent}>
          <DirectionText className={styles.title}>
            {advertisement.title}
          </DirectionText>
          <DirectionText className={styles.description}>
            {advertisement.description}
          </DirectionText>
        </div>

        {advertisement.cta_link && (
          <div className={styles.actions}>
            <Button className={styles.ctaButton} onClick={handleCTAClick}>
              {i18n.language === "ar" ? (
                <>
                  {advertisement?.cta_button_text_ar || "اعرف المزيد"}
                  <FaArrowLeft />
                </>
              ) : (
                <>
                  {advertisement?.cta_button_text || "Learn More"}
                  <FaArrowRight />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </MyModal>
  )
}
